import { createAction, createListenerMiddleware, type TypedStartListening } from '@reduxjs/toolkit';
import { getLogger } from 'app-components/utils/core';

import type { AppDispatch, RootState } from 'store';

const log = getLogger('store/listenerMiddleware');

type ListenerErrorReporter = (error: unknown, raisedBy: 'effect' | 'predicate') => void;

let reportListenerError: ListenerErrorReporter = () => {};

// A listener stays registered after it throws, so a deterministic failure repeats on every matching
// action. Report each message and throw site once per page load; Bugsnag keeps only a few events per page.
const reportedErrors = new Set<string>();

/** The app reports listener errors to Bugsnag; until it sets a reporter, they are only logged. */
export const setListenerErrorReporter = (reporter: ListenerErrorReporter): void => {
  reportListenerError = reporter;
  reportedErrors.clear();
};

// Never throws: onError runs inside RTK, which would rethrow from a timer.
const describe = (value: unknown): string => {
  try {
    return typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
};

const errorKey = (error: unknown): string => {
  const fields: object = typeof error === 'object' && error !== null ? error : {};
  const message = describe('message' in fields ? fields.message : error);
  // V8 stacks start with the message, so drop it first. The first frame left is the throw site; the
  // frames under it are whoever dispatched the action.
  const stack = 'stack' in fields && typeof fields.stack === 'string' ? fields.stack.replace(message, '') : '';
  const throwSite = stack.split('\n').find((line) => /:\d+:\d+\)?$/.test(line)) ?? '';
  return `${message}@${throwSite}`;
};

export const listenerMiddleware = createListenerMiddleware({
  onError: (error, { raisedBy }) => {
    // Supplying onError replaces RTK's console.error, so keep it for dev and tests.
    // biome-ignore lint/suspicious/noConsole: last-resort log for a listener that threw
    console.error('listenerMiddleware/error', error, { raisedBy });
    const key = errorKey(error);
    if (reportedErrors.has(key)) return;
    reportedErrors.add(key);
    reportListenerError(error, raisedBy);
  },
});

export const initListeners = createAction('INIT_LISTENERS');

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;

export const startAppListening = listenerMiddleware.startListening as AppStartListening;

log.info('Listener middleware created');
